/**
 * Bulk-replace members drawer. The reducer applies decisions 12+13:
 * removals cancel that member's not-started cohort assignments, and
 * additions back-fill every Active pairing with the collision rule —
 * the footer copy says so, because the consequences are invisible here.
 */
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { useMemo, useState } from 'react';
import type { CohortEntityRow } from '@/hooks/useCohortEntities';
import { GatedButton } from '../GatedButton';
import { MemberPicker } from './MemberPicker';

export function EditMembersDrawer({
  cohort,
  userEmails,
  busy,
  onCancel,
  onSave,
}: {
  cohort: CohortEntityRow;
  userEmails: string[];
  busy: boolean;
  onCancel: () => void;
  onSave: (emails: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(cohort.members),
  );
  const [query, setQuery] = useState('');
  // Roster members who are no longer on the site's user list must remain
  // selectable (removable) — union them in so saving cannot silently drop
  // someone just because their user row moved sites.
  const allEmails = useMemo(
    () => Array.from(new Set([...userEmails, ...cohort.members])).sort(),
    [userEmails, cohort.members],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? allEmails.filter((e) => e.toLowerCase().includes(q)) : allEmails;
  }, [allEmails, query]);
  const added = Array.from(selected).filter((e) => !cohort.members.includes(e)).length;
  const removed = cohort.members.filter((m) => !selected.has(m)).length;

  return (
    <>
      <div
        onClick={onCancel}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 40 }}
      />
      <aside
        role="dialog"
        aria-label={`Edit members of ${cohort.name}`}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 400,
          maxWidth: '92vw',
          background: 'var(--panel)',
          color: 'var(--text)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.4)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontWeight: 600 }}>Members · {cohort.name}</div>
          <button
            type="button"
            className="icon-btn"
            onClick={onCancel}
            aria-label="Close"
            style={{ width: 22, height: 22 }}
          >
            <Icon of={Icons.action.remove} size={14} />
          </button>
        </header>
        <div
          style={{
            padding: 12,
            display: 'grid',
            gap: 8,
            overflowY: 'auto',
            flex: 1,
            alignContent: 'start',
          }}
        >
          <input
            type="search"
            className="inp"
            placeholder="Filter…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter members"
          />
          <MemberPicker
            emails={filtered}
            selected={selected}
            onToggle={(email) =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(email)) next.delete(email);
                else next.add(email);
                return next;
              })
            }
          />
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            Removing a member cancels their not-started assignments under
            this cohort (started runs continue; adopted individual
            assignments are handed back). Adding one back-fills every
            active orientation of this cohort.
          </div>
        </div>
        <footer
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <GatedButton
            className="btn primary"
            busy={busy}
            reason={added === 0 && removed === 0 ? 'No changes to save — tick or untick a member first' : null}
            onClick={() => onSave(Array.from(selected))}
          >
            {busy
              ? 'Saving…'
              : `Save${added > 0 ? ` +${added}` : ''}${removed > 0 ? ` −${removed}` : ''}`}
          </GatedButton>
        </footer>
      </aside>
    </>
  );
}
